'use client';

import { XIcon } from '@ctct/summit-icons';
import { cva } from 'class-variance-authority';
import { type ComponentPropsWithoutRef, forwardRef, useCallback, useEffect, useState } from 'react';

import { useSummitI18n } from '../../i18n/SummitI18nProvider';
import { cn } from '../../lib/utils';
import { type Attribute, type createStyleType } from '../../theme/types';
import { Card } from '../Card';
import { IconOnlyButton } from '../IconOnlyButton';

const themeableClasses = [
  'r:bg-(--bg)',
  'r:border-(--border)',
  'r:[&_.rds-toast-icon>svg]:outline-(--outline)',
  'r:[&_.rds-toast-icon>svg]:text-(--icon)',
] as const;

type ToastStyle = ReturnType<typeof createStyleType<typeof themeableClasses>>;

type ToastColor = Extract<Attribute, 'dark' | 'error' | 'success' | 'warning'>;

const colorVariants: Record<ToastColor, string> = {
  dark: `
    r:[--bg:theme(toast.bg.dark)]
    r:[--border:theme(toast.border.dark)]
    r:[--icon:theme(toast.icon.dark)]
    r:[--outline:theme(toast.outline.dark)]
    r:[&_.rds-toast-close>button]:text-white
    r:[&_.rds-toast-content]:[--sds-text-dark:theme(colors.white)]
  `,
  error: `
    r:[--bg:theme(toast.bg.error)]
    r:[--border:theme(toast.border.error)]
    r:[--icon:theme(toast.icon.error)]
    r:[--outline:theme(toast.outline.error)]
  `,
  success: `
    r:[--bg:theme(toast.bg.success)]
    r:[--border:theme(toast.border.success)]
    r:[--icon:theme(toast.icon.success)]
    r:[--outline:theme(toast.outline.success)]
  `,
  warning: `
    r:[--bg:theme(toast.bg.warning)]
    r:[--border:theme(toast.border.warning)]
    r:[--icon:theme(toast.icon.warning)]
    r:[--outline:theme(toast.outline.warning)]
  `,
};

const toastVariants = cva(
  `
    ${themeableClasses.join(' ')}
    r:z-overlay-content
    r:p-3.5
    r:text-sm
    r:shadow-md
    r:[&_.rds-toast-content]:flex

    r:[&_.rds-toast-icon]:relative
    r:[&_.rds-toast-icon>svg]:rounded-full
    r:[&_.rds-toast-icon>svg]:outline
    r:[&_.rds-toast-icon>svg]:outline-2
    r:[&_.rds-toast-icon>svg]:outline-offset-2

    r:[&_.rds-toast-content]:flex-col
    r:[&_.rds-toast-close>button]:p-0
    r:[&_.rds-toast-close>button]:h-auto
  `,
  {
    variants: {
      color: colorVariants,
      size: {
        sm: 'r:w-[352px]',
        md: 'r:w-[420px]',
      },
    },
    defaultVariants: {
      color: 'dark',
      size: 'sm',
    },
  }
);

interface ToastProps extends ComponentPropsWithoutRef<'div'> {
  /** The kind of toast to convey. */
  color?: ToastColor;
  /** Indicate how the toast content should be displayed. */
  size?: 'sm' | 'md';
  /** Set inline styles, including the component's built-in CSS variables.  */
  style?: ToastStyle;
  /** Callback for when the toast is closed. */
  onClose?: () => void;
  /** If provided a number, will call the onClose callback after that many milliseconds.
   * Pass "none" to prevent autoclose. */
  autocloseMs?: number | 'none';
  /** If true, the close button will not be shown. */
  shouldHideCloseButton?: boolean;
}

/** A succinct message that is displayed temporarily. */
const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      color = 'dark',
      size = 'sm',
      onClose,
      autocloseMs = 3000,
      shouldHideCloseButton = false,
      className,
      children,
      ...props
    },
    forwardedRef
  ) => {
    const { tCore } = useSummitI18n();
    const openStyles = 'r:opacity-100 r:translate-y-0';
    const closedStyles = 'r:opacity-0 r:-translate-y-2';
    const [transitionStyles, setTransitionStyles] = useState(closedStyles);
    const handleClose = useCallback(() => {
      if (onClose) {
        setTransitionStyles(closedStyles);
        setTimeout(() => {
          onClose();
        }, 150);
      }
    }, [onClose]);

    useEffect(() => {
      setTransitionStyles(openStyles);
    }, []);

    useEffect(() => {
      if (autocloseMs !== 'none' && onClose) {
        const timer = setTimeout(() => {
          handleClose();
        }, autocloseMs);
        return () => clearTimeout(timer);
      }
    }, [autocloseMs, handleClose, onClose]);

    return (
      <Card
        ref={forwardedRef}
        role="alert"
        data-closeable={!!onClose}
        className={cn(
          'rds-toast r:transition-[opacity,transform] r:duration-150',
          transitionStyles,
          toastVariants({ color, size }),
          className
        )}
        data-sds-id="toast"
        {...props}
      >
        <div className="rds-toast-container r:flex r:flex-row r:gap-x-2 r:m-auto">
          {children}
          {!shouldHideCloseButton && (
            <div className="rds-toast-close r:ml-auto r:-mr-3">
              <IconOnlyButton
                className="r:rounded-full r:hover:bg-[unset]"
                emphasis="low"
                color="neutral"
                onClick={handleClose}
                aria-label={tCore('toast.closeAriaLabel')}
              >
                <XIcon className="r:w-5 r:h-5" />
              </IconOnlyButton>
            </div>
          )}
        </div>
      </Card>
    );
  }
);
Toast.displayName = 'Toast';

export { Toast, type ToastColor, type ToastProps };
